import React, { useCallback, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import "highlight.js/styles/atom-one-dark.css";
import api from "../lib/api";

const lowlight = createLowlight(common);
import {
  Bold, Italic, Strikethrough, Code as CodeIcon,
  Heading1, Heading2, List, ListOrdered, Quote, Minus,
  Link as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon,
  FileText, Undo, Redo,
} from "lucide-react";

function ToolbarBtn({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`xs-tt-btn ${active ? "active" : ""}`}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function TipTapEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      CodeBlockLowlight.configure({ lowlight, HTMLAttributes: { class: "xs-code" } }),
      Image.configure({ allowBase64: false, HTMLAttributes: { class: "xs-tt-img" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Placeholder.configure({ placeholder: placeholder || "write your post… markdown-style shortcuts supported (e.g. ## heading, **bold**)" }),
      Youtube.configure({ controls: true, width: 640, height: 360, HTMLAttributes: { class: "xs-tt-yt" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // External value updates (e.g. load post into editor)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertImage = useCallback(async () => {
    if (!editor) return;
    const pick = document.createElement("input");
    pick.type = "file";
    pick.accept = "image/*";
    pick.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = data.url.startsWith("http")
          ? data.url
          : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch (err) {
        alert("image upload failed");
      }
    };
    pick.click();
  }, [editor]);

  const insertLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("url:", prev);
    if (url === null) return;
    if (url === "") editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const insertYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("youtube url:");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url });
  }, [editor]);

  const insertPdf = useCallback(() => {
    if (!editor) return;
    const pick = document.createElement("input");
    pick.type = "file";
    pick.accept = "application/pdf,.pdf";
    pick.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        editor.chain().focus().insertContent({
          type: "paragraph",
          content: [{
            type: "text",
            text: file.name,
            marks: [{ type: "link", attrs: { href: data.url } }],
          }],
        }).run();
      } catch (err) {
        alert(err.response?.data?.detail || "PDF upload failed");
      }
    };
    pick.click();
  }, [editor]);

  // Drag-and-drop images into editor
  const onDrop = useCallback(async (e) => {
    if (!editor || !e.dataTransfer?.files?.length) return;
    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    e.preventDefault();
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = data.url.startsWith("http")
          ? data.url
          : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      } catch {}
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="xs-tt" data-testid="tiptap-editor">
      <div className="xs-tt-toolbar">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (⌘B)" testId="tt-bold"><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (⌘I)" testId="tt-italic"><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strike" testId="tt-strike"><Strikethrough size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code" testId="tt-code"><CodeIcon size={14} /></ToolbarBtn>
        <span className="xs-tt-sep" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="H1" testId="tt-h1"><Heading1 size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2" testId="tt-h2"><Heading2 size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list" testId="tt-ul"><List size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered" testId="tt-ol"><ListOrdered size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId="tt-quote"><Quote size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block" testId="tt-codeblock"><CodeIcon size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testId="tt-hr"><Minus size={14} /></ToolbarBtn>
        <span className="xs-tt-sep" />
        <ToolbarBtn onClick={insertLink} active={editor.isActive("link")} title="Link" testId="tt-link"><LinkIcon size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={insertImage} title="Image (upload)" testId="tt-img"><ImageIcon size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={insertPdf} title="PDF (upload)" testId="tt-pdf"><FileText size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={insertYoutube} title="YouTube" testId="tt-yt"><YoutubeIcon size={14} /></ToolbarBtn>
        <span className="xs-tt-sep" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (⌘Z)" testId="tt-undo"><Undo size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="tt-redo"><Redo size={14} /></ToolbarBtn>
      </div>
      <div className="xs-tt-surface" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
